import React, { useEffect, useState } from 'react';
import {
  Pagination,
  Button,
  Modal,
  Form,
  Input,
  Spin,
  Select,
  DatePicker,
  TimePicker,
  Descriptions,
  Tooltip,
  Card,
  Avatar,
} from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import {
  PlusOutlined,
  EyeFilled,
  CheckCircleFilled,
  DeleteFilled,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import CustomModal from '../../../components/common/CustomModal';
import { DataTable, SortableTableHeader } from '../../../components/common';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  getRound,
  getSport,
  removeGame,
  addGame,
  getTeam,
  getGame,
  updateGame,
  sendGameStartNotification,
} from '../../../store/slices/adminSlice';
import moment, { Moment } from 'moment';
import toastr from 'toastr';

const { confirm } = Modal;
const { Option } = Select;
const { Meta } = Card;

// TypeScript interfaces
interface SportData {
  _id: string;
  sportname: string;
  type?: string;
  startDate?: string;
  endDate?: string;
}

interface RoundData {
  _id: string;
  roundno: number;
  roundname: string;
  startDate: string;
  endDate: string;
  sport: SportData;
}

interface TeamData {
  _id: string;
  teamname: string;
  teamLogo?: string;
  sport: SportData;
}

interface GameData {
  _id: string;
  sport: SportData;
  round: RoundData;
  homeTeam: TeamData;
  awayTeam: TeamData;
  gameDate: string;
  gameTime: string;
  gameState: string;
  points?: number;
  homeTeamPoints?: number;
  awayTeamPoints?: number;
  homeSeasonPoints?: number;
  awaySeasonPoints?: number;
  homeTopTipperPoints?: number;
  awayTopTipperPoints?: number;
  drawPoints?: number;
  winningTeam: string;
  season?: string;
  eventId?: string;
  kingbotTipping?: string;
  gameEndDateTime?: string;
}

interface WinnerData {
  id: number;
  name: string;
}

interface AddGameFormValues {
  sport: string;
  round: string;
  timezone: string;
  date: Moment;
  time: Moment;
  hometeampoints: string;
  awayteampoints: string;
  drawSport: string;
  hometeam: string;
  awayteam: string;
  eventId: string;
  kingbotTipping: string;
}

interface EditGameFormValues {
  esport: string;
  eround: string;
  etimezone: string;
  edate: Moment;
  etime: Moment;
  ehometeampoints: string;
  eawayteampoints: string;
  edrawSport: string;
  ehometeam: string;
  eawayteam: string;
  eEventId: string;
  ekingbotTipping: string;
  ewinner: string;
}

const Game: React.FC = () => {
  const dispatch = useAppDispatch();
  const [addForm] = Form.useForm<AddGameFormValues>();
  const [editForm] = Form.useForm<EditGameFormValues>();

  // Redux state
  const roundData = useAppSelector((state) => state.admin.roundData) as RoundData[];
  const totalCount = useAppSelector((state) => state.admin.gameCount);
  const sportData = useAppSelector((state) => state.admin.sportData) as unknown as SportData[];
  const teamData = useAppSelector((state) => state.admin.teamData) as unknown as TeamData[];
  const gameData = useAppSelector((state) => state.admin.gameData) as GameData[];

  // Local state
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [visible, setVisible] = useState<boolean>(false);
  const [viewModalVisible, setViewModalVisible] = useState<boolean>(false);
  const [resultModalVisible, setResultModalVisible] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [selectRoundData, setSelectRoundData] = useState<RoundData[]>([]);
  const [homeTeamData, setHomeTeamData] = useState<TeamData[]>([]);
  const [selectHomeTeamData, setSelectHomeTeamData] = useState<TeamData[]>([]);
  const [awayTeamData, setAwayTeamData] = useState<TeamData[]>([]);
  const [selectAwayTeamData, setSelectAwayTeamData] = useState<TeamData[]>([]);
  const [sportId, setSportId] = useState<string>('');
  const [roundId, setRoundId] = useState<string>('');
  const [homeTeamId, setHomeTeamId] = useState<string>('');
  const [awayTeamId, setAwayTeamId] = useState<string>('');
  const [sortValue] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<number | ''>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [viewGameData, setViewGameData] = useState<GameData | any>({});
  const [viewHomeTeamData, setViewHomeTeamData] = useState<TeamData | any>({});
  const [viewAwayTeamData, setViewAwayTeamData] = useState<TeamData | any>({});
  const [viewRoundData, setViewRoundData] = useState<RoundData | any>({});
  const [viewSportData, setViewSportData] = useState<SportData | any>({});
  const [winner, setWinner] = useState<string>('');
  const [editGameDate, setEditGameDate] = useState<Date | string>('');
  const [editGameTime, setEditGameTime] = useState<Date | string>('');
  const [editGameState, setEditGameState] = useState<string>('');
  const [selectedSeason] = useState<string>('current');
  const [, setEditDrawSport] = useState<string>('');
  const [, setSportType] = useState<string>('');
  const [selectedSport, setSelectedSport] = useState<SportData | any>({});
  const [selectedKingbotTipping, setSelectedKingbotTipping] = useState<string>('');

  const selectWinnerData: WinnerData[] = [
    { id: 1, name: 'Home' },
    { id: 2, name: 'Away' },
    { id: 3, name: 'Draw' },
  ];

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };

  // Load data on mount
  useEffect(() => {
    const obj = {
      page: currentPage,
      sortValue: sortValue,
      sortOrder: sortOrder,
      season: 'current',
    };
    const selectionObj = {
      count: 1000,
      season: 'current',
      page: 0,
    };
    const getSportList = {
      count: 1000,
      isActive: true,
      page: 0,
    };

    dispatch(getSport(getSportList));
    dispatch(getRound(selectionObj));
    dispatch(getTeam(selectionObj));
    dispatch(getGame(obj));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update loading state when game data is received
  useEffect(() => {
    if (gameData) {
      setLoading(false);
    }
  }, [gameData]);

  const formatDate = (date: string | Date): string => {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('/');
  };

  const isDateBetween = (startDate: string, endDate: string, checkDate: Date | Moment): boolean => {
    const from = new Date(startDate);
    from.setHours(0, 0, 0, 0);
    const to = new Date(endDate);
    to.setHours(23, 55, 0, 0);
    const check = new Date(checkDate as any);

    return check >= from && check <= to;
  };

  const showModal = (
    id: string,
    sport?: SportData,
    round?: RoundData,
    date?: string,
    time?: string,
    points?: number,
    homeTeamPoints?: number,
    awayTeamPoints?: number,
    homeSeasonPoints?: number,
    awaySeasonPoints?: number,
    homeTeam?: TeamData,
    awayTeam?: TeamData
  ) => {
    setVisible(true);
    setSportId(id);

    if (id !== '') {
      let filteredRoundData = roundData.filter((r) => r.sport._id === sport?._id);
      let filteredTeamData = teamData.filter((t) => t.sport._id === sport?._id);

      setSportId(sport?._id || '');
      setSelectRoundData(filteredRoundData);
      setHomeTeamData(filteredTeamData);
      setSelectHomeTeamData(filteredTeamData);
      setAwayTeamData(filteredTeamData);
      setSelectAwayTeamData(filteredTeamData);

      const mydate = formatDate(date || '');

      addForm.setFieldsValue({
        sport: sport?._id,
        round: round?._id,
        date: moment(mydate, 'YYYY/MM/DD'),
        hometeampoints: String(homeTeamPoints || ''),
        awayteampoints: String(awayTeamPoints || ''),
        hometeam: homeTeam?._id,
        awayteam: awayTeam?._id,
      });
    } else {
      addForm.resetFields();
    }
  };

  const handleSendGameStartNotification = (gameId: string) => {
    const obj = {
      gameId: gameId,
      selectedSeason: selectedSeason,
    };
    dispatch(sendGameStartNotification(obj));
  };

  const showViewModal = (data: GameData) => {
    setViewModalVisible(true);
    setViewGameData(data);
    setViewHomeTeamData(data.homeTeam);
    setViewAwayTeamData(data.awayTeam);
    setViewRoundData(data.round);
    setViewSportData(data.sport);
  };

  const showResultModal = (data: GameData) => {
    if (data.winningTeam === '') {
      setResultModalVisible(true);
      setViewGameData(data);
      setViewHomeTeamData(data.homeTeam);
      setViewAwayTeamData(data.awayTeam);
      setViewSportData(data.sport);
    } else {
      toastr.info('Winner already declared');
    }
  };

  const showEditModal = (data: GameData) => {
    setSportType(data.sport.type || '');
    setSelectedSport(data.sport);

    editForm.resetFields();

    let filteredRoundData = roundData.filter((r) => r.sport._id === data.sport._id);
    let filteredHomeTeamData = teamData.filter((t) => t.sport._id === data.sport._id);
    let filteredAwayTeamData = teamData.filter((t) => t.sport._id === data.sport._id);
    let filteredSelectAwayTeamData = filteredAwayTeamData.filter((t) => t._id !== data.homeTeam._id);
    let filteredSelectHomeTeamData = filteredHomeTeamData.filter((t) => t._id !== data.awayTeam._id);

    setEditModalVisible(true);
    setViewGameData(data);
    setViewSportData(data.sport);
    setEditGameDate(data.gameDate);
    setEditGameTime(new Date(data.gameTime));
    setSelectRoundData(filteredRoundData);
    setHomeTeamData(filteredHomeTeamData);
    setSelectHomeTeamData(filteredSelectHomeTeamData);
    setAwayTeamData(filteredAwayTeamData);
    setSelectAwayTeamData(filteredSelectAwayTeamData);
    setEditGameState(data.gameState);
    setEditDrawSport(data.drawPoints === undefined || data.drawPoints === null ? '' : String(data.drawPoints));

    editForm.setFieldsValue({
      esport: data.sport._id,
      eround: data.round._id,
      ehometeampoints: String(data.homeTeamPoints || ''),
      eawayteampoints: String(data.awayTeamPoints || ''),
      edrawSport: data.drawPoints ? String(data.drawPoints) : '',
      eEventId: data.eventId || '',
      ehometeam: data.homeTeam._id,
      eawayteam: data.awayTeam._id,
      ewinner: data.winningTeam,
      ekingbotTipping: data.kingbotTipping || '',
    });
    setWinner(data.winningTeam);
  };

  const handleSportSelect = (value: string) => {
    let filteredRoundData = roundData.filter((r) => r.sport._id === value);
    let filteredSportData = sportData.filter((s) => s._id === value);
    let filteredTeamData = teamData.filter((t) => t.sport._id === value);

    addForm.setFieldsValue({
      round: undefined as any,
      hometeam: undefined as any,
      awayteam: undefined as any,
    });
    editForm.setFieldsValue({
      eround: undefined as any,
      ehometeam: undefined as any,
      eawayteam: undefined as any,
    });

    setSportId(value);
    setSelectedSport(filteredSportData[0] || {});
    setSelectRoundData(filteredRoundData);
    setHomeTeamData(filteredTeamData);
    setSelectHomeTeamData(filteredTeamData);
    setAwayTeamData(filteredTeamData);
    setSelectAwayTeamData(filteredTeamData);
  };

  const handleRoundSelect = (value: string) => {
    setRoundId(value);
  };

  const handleHomeTeamSelect = (value: string) => {
    let filteredAwayTeamData = awayTeamData.filter((t) => t._id !== value);
    setHomeTeamId(value);
    setSelectAwayTeamData(filteredAwayTeamData);
  };

  const handleAwayTeamSelect = (value: string) => {
    let filteredHomeTeamData = homeTeamData.filter((t) => t._id !== value);
    setAwayTeamId(value);
    setSelectHomeTeamData(filteredHomeTeamData);
  };

  const handleWinnerSelect = (value: string) => {
    setWinner(value);
  };

  const handleOk = async () => {
    try {
      const values = await addForm.validateFields();

      const gameTime = new Date(values.time as any);
      const gameDateTime = new Date(values.date as any);
      gameDateTime.setHours(gameTime.getHours(), gameTime.getMinutes(), 0);

      const obj = {
        sportId: sportId,
        date: gameDateTime,
        time: gameDateTime,
        gameState: 'open',
        round: roundId,
        homeTeamPoints: parseFloat(values.hometeampoints),
        awayTeamPoints: parseFloat(values.awayteampoints),
        eventId: values.eventId,
        homeTeam: homeTeamId,
        awayTeam: awayTeamId,
        winningTeam: '',
        season: 'current',
        selectedSeason: selectedSeason,
        drawPoints: parseInt(values.drawSport),
        kingbotTipping: selectedKingbotTipping,
      };

      const round = roundData.find((r) => r._id === roundId);
      if (round && isDateBetween(round.startDate, round.endDate, values.date)) {
        setVisible(false);
        setCurrentPage(0);
        dispatch(addGame(obj));
      } else {
        toastr.warning('Please select date between round date');
      }
    } catch (error) {
      toastr.warning('Please enter required details');
    }
  };

  const handleViewModalOk = () => {
    setViewModalVisible(false);
  };

  const handleResultModalOk = () => {
    setResultModalVisible(false);
    setCurrentPage(0);

    const gameEndDateTime = new Date();
    gameEndDateTime.setHours(gameEndDateTime.getHours(), gameEndDateTime.getMinutes(), 0);

    const obj = {
      gameId: viewGameData._id,
      sportId: viewSportData._id,
      gameState: 'finished',
      winningTeam: winner,
      selectedSeason: selectedSeason,
      gameEndDateTime: gameEndDateTime,
    };
    dispatch(addGame(obj));
  };

  const handleEditModalOk = async () => {
    try {
      const values = await editForm.validateFields();

      const gameTime = new Date(editGameTime);
      const gameDateTime = new Date(editGameDate);
      gameDateTime.setHours(gameTime.getHours(), gameTime.getMinutes(), 0);

      const obj = {
        gameId: viewGameData._id,
        gameDate: gameDateTime,
        gameTime: gameDateTime,
        sport: values.esport,
        round: values.eround,
        homeTeamPoints: parseFloat(values.ehometeampoints),
        awayTeamPoints: parseFloat(values.eawayteampoints),
        eventId: values.eEventId,
        homeTeam: values.ehometeam,
        awayTeam: values.eawayteam,
        winningTeam: viewGameData.winningTeam,
        drawPoints: values.edrawSport,
        gameState: editGameState,
        selectedSeason: selectedSeason,
        kingbotTipping: selectedKingbotTipping,
      };

      const round = roundData.find((r) => r._id === values.eround);
      if (round && isDateBetween(round.startDate, round.endDate, new Date(editGameDate))) {
        if (winner && editGameState === 'finished') {
          toastr.warning('Game already finished');
        } else {
          setEditModalVisible(false);
          setCurrentPage(0);
          dispatch(updateGame(obj));
        }
      } else {
        toastr.warning('Please select date between round date');
      }
    } catch (error) {
      toastr.warning('Please enter required details');
    }
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const handleViewModalCancel = () => {
    setViewModalVisible(false);
  };

  const handleResultModalCancel = () => {
    setResultModalVisible(false);
  };

  const handleEditModalCancel = () => {
    setEditModalVisible(false);
  };

  const onEditDateChange = (date: Moment | null, dateString: string) => {
    setEditGameDate(new Date(dateString));
  };

  const onEditTimeChange = (time: Moment | null, timeString: string) => {
    if (time) {
      setEditGameTime(new Date(time.toDate()));
    }
  };

  const showConfirm = (gameId: string) => {
    const obj = {
      gameId: gameId,
      selectedSeason: selectedSeason,
    };
    confirm({
      title:
        "Are you sure you want to delete this game. If these teams already played, then this may cause issues. If you need to make a change to this game, then edit it (don't delete it)",
      content: '',
      onOk() {
        setCurrentPage(0);
        dispatch(removeGame(obj));
      },
      onCancel() {},
    });
  };

  const showGameStateConfirm = (gameId: string, sportIdParam: string) => {
    const obj = {
      gameId: gameId,
      gameState: 'started',
      selectedSeason: selectedSeason,
      sportId: sportIdParam,
    };
    confirm({
      title: 'Are you sure you want to start this game',
      content: '',
      onOk() {
        setCurrentPage(0);
        dispatch(addGame(obj));
      },
      onCancel() {},
    });
  };

  const changePage = (page: number, pageSize?: number) => {
    setCurrentPage(page - 1);
    const obj = {
      page: page - 1,
      sortValue: sortValue,
      sortOrder: sortOrder,
      season: selectedSeason,
      sport: sportId,
    };
    dispatch(getGame(obj));
  };

  const sortData = (sortVal: string) => {
    const newSortOrder = sortOrder === -1 ? 1 : -1;
    setSortOrder(newSortOrder);
    const obj = {
      page: currentPage,
      sortValue: sortVal,
      sortOrder: newSortOrder,
      selectedSeason: selectedSeason,
    };
    dispatch(getGame(obj));
  };

  const handleMenuClickSport = (value: string) => {
    const obj = {
      sport: value,
      season: 'current',
    };
    setSportId(value);

    const obj1 = {
      page: currentPage,
      sortValue: sortValue,
      sortOrder: sortOrder,
      season: 'current',
    };

    if (value === '') {
      dispatch(getGame(obj1));
    } else {
      dispatch(getGame(obj));
    }
  };

  const handleKingbotTippingSelect = (value: string) => {
    setSelectedKingbotTipping(value);
  };

  // Render sport options
  const sportOptions = sportData.map((x, index) => (
    <Option value={x._id} key={index}>
      {x.sportname}
    </Option>
  ));

  // Render mobile game cards
  const renderMobileGameCards = () => {
    return gameData.map((game, index) => {
      const isGameStarted = game.gameState !== 'open';
      const isWinnerDeclared = game.winningTeam !== '';

      // Determine border color based on status
      let borderColor = '#d9d9d9'; // Gray - Not Started
      if (isWinnerDeclared) {
        borderColor = '#52c41a'; // Green - Declared
      } else if (isGameStarted) {
        borderColor = '#fa8c16'; // Orange - Started
      }

      return (
        <div key={index} className="game-card" style={{ borderLeftColor: borderColor }}>
          <div className="game-card-header">
            <span className="game-card-sport">{game.sport.sportname}</span>
            <span className="game-card-round-badge">Round {game.round.roundno}</span>
          </div>

          <div className="game-card-teams">
            <span className="team-name">{game.homeTeam.teamname}</span>
            <span className="vs-text">vs</span>
            <span className="team-name">{game.awayTeam.teamname}</span>
          </div>

          <div className="game-card-footer">
            <span className="game-card-week">{game.round.roundname}</span>
            <div className="game-card-actions">
              <Tooltip title="View Details">
                <Button type="link" size="small" onClick={() => showViewModal(game)}>
                  <EyeOutlined />
                </Button>
              </Tooltip>
              <Tooltip title="Edit">
                <Button type="link" size="small" onClick={() => showEditModal(game)}>
                  <EditOutlined />
                </Button>
              </Tooltip>
              <Tooltip title="Delete">
                <Button type="link" size="small" onClick={() => showConfirm(game._id)}>
                  <DeleteOutlined />
                </Button>
              </Tooltip>
              {game.gameState === 'open' && (
                <Tooltip title="Start Game">
                  <Button type="link" size="small" onClick={() => showGameStateConfirm(game._id, game.sport._id)}>
                    <PlayCircleOutlined />
                  </Button>
                </Tooltip>
              )}
              <Tooltip title={isWinnerDeclared ? 'Winner Declared' : 'Declare Winner'}>
                <Button
                  type="link"
                  size="small"
                  onClick={() => !isWinnerDeclared && showResultModal(game)}
                  disabled={isWinnerDeclared}
                  style={{ color: isWinnerDeclared ? '#52c41a' : undefined }}
                >
                  <CheckCircleOutlined />
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
      );
    });
  };

  // Common icon style
  const iconStyle = { color: '#666' };

  // Define table columns for DataTable
  const columns = [
    {
      key: 'sport',
      title: (
        <SortableTableHeader
          label="Sports"
          sortKey="sport.sportname"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: GameData) => item.sport.sportname,
    },
    {
      key: 'roundno',
      title: (
        <SortableTableHeader
          label="Round No"
          sortKey="round.roundno"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: GameData) => item.round.roundno,
    },
    {
      key: 'roundname',
      title: (
        <SortableTableHeader
          label="Round"
          sortKey="round.roundname"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: GameData) => item.round.roundname,
    },
    {
      key: 'hometeam',
      title: (
        <SortableTableHeader
          label="Home Team"
          sortKey="hometeam.teamname"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: GameData) => item.homeTeam.teamname,
    },
    {
      key: 'awayteam',
      title: (
        <SortableTableHeader
          label="Away Team"
          sortKey="awayteam.teamname"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: GameData) => item.awayTeam.teamname,
    },
    {
      key: 'actions',
      title: 'Action',
      className: 'action-icons',
      render: (item: GameData) => (
        <>
          <Tooltip placement="top" title="View Details">
            <Button type="link" className="btn-sm mr-2" onClick={() => showViewModal(item)}>
              <EyeFilled style={iconStyle} />
            </Button>
          </Tooltip>
          <Tooltip placement="top" title="Declare Winner">
            <Button type="link" className="btn-sm mr-2" onClick={() => showResultModal(item)}>
              <CheckCircleFilled style={iconStyle} />
            </Button>
          </Tooltip>
          <Tooltip placement="top" title="Delete">
            <Button type="link" className="btn-sm mr-2" onClick={() => showConfirm(item._id)}>
              <DeleteFilled style={iconStyle} />
            </Button>
          </Tooltip>
          <Tooltip placement="top" title="Edit">
            <Button type="link" className="btn-sm mr-2" onClick={() => showEditModal(item)}>
              <i className="fa fa-pencil" style={iconStyle}></i>
            </Button>
          </Tooltip>
          {item.gameState === 'open' && (
            <Tooltip placement="top" title="Send Game Start Notification">
              <Button type="link" className="btn-sm mr-2" onClick={() => handleSendGameStartNotification(item._id)}>
                <i className="fa fa-telegram" style={iconStyle}></i>
              </Button>
            </Tooltip>
          )}
          {item.gameState === 'open' && (
            <Tooltip placement="top" title="Start Game">
              <Button type="link" className="btn-sm mr-2" onClick={() => showGameStateConfirm(item._id, item.sport._id)}>
                <i className="fa fa-hourglass-start" style={iconStyle}></i>
              </Button>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <div className="box box-default mb-4">
        <PageHeader
          prefixedClassName="ant-page-header"
          title="Game"
          subTitle=""
          extra={[
            <Select
              key="2"
              defaultValue=""
              placeholder="Season"
              style={{ width: 150 }}
              className="mobile-bottom-space"
              onChange={handleMenuClickSport}
            >
              <Option value="">All</Option>
              {sportOptions}
            </Select>,
            <Button
              key="1"
              type="default"
              onClick={() => showModal('', undefined, undefined)}
            >
              <PlusOutlined />
              Add Game
            </Button>,
          ]}
        />
        <Spin spinning={loading} delay={500}>
          <div className="box-body">
            {/* Mobile status legend */}
            <div className="mobile-card-view">
              <div className="game-status-legend">
                <span className="legend-item">
                  <span className="legend-dot started"></span> Started
                </span>
                <span className="legend-item">
                  <span className="legend-dot not-started"></span> Not Started
                </span>
                <span className="legend-item">
                  <span className="legend-dot declared"></span> Declared
                </span>
              </div>
              {renderMobileGameCards()}
            </div>

            {/* Desktop table view - using DataTable component */}
            <DataTable
              columns={columns}
              data={gameData}
              loading={false}
              showPagination={false}
              rowKey="_id"
              wrapperClassName="desktop-table-view"
            />
            <Pagination
              style={{ textAlign: 'right' }}
              pageSize={10}
              current={currentPage + 1}
              defaultCurrent={currentPage + 1}
              total={totalCount ? totalCount : 1}
              onChange={changePage}
            />

            {/* Add Game Modal */}
            <CustomModal
              handleCancel={handleCancel}
              handleOk={handleOk}
              visible={visible}
              title="Add Game"
              backBtnText="Back"
              submitBtnText="Submit"
            >
              <Form form={addForm} {...layout}>
                <Form.Item
                  label="Select Sports"
                  name="sport"
                  rules={[
                    {
                      required: true,
                      message: 'Please select sports',
                    },
                  ]}
                >
                  <Select onChange={handleSportSelect}>{sportOptions}</Select>
                </Form.Item>

                <Form.Item
                  label="Select Round"
                  name="round"
                  rules={[
                    {
                      required: true,
                      message: 'Please select round',
                    },
                  ]}
                >
                  <Select onChange={handleRoundSelect}>
                    {selectRoundData.map((x, index) => (
                      <Option value={x._id} key={index}>
                        {x.roundname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Timezone"
                  name="timezone"
                  initialValue="Australia/Sydney"
                  rules={[
                    {
                      required: false,
                      message: 'Please enter timezone',
                    },
                  ]}
                >
                  <Input placeholder="Timezone" disabled />
                </Form.Item>

                <Form.Item
                  label="Date"
                  name="date"
                  rules={[{ required: true, message: 'Please enter date' }]}
                >
                  <DatePicker placeholder="Date" />
                </Form.Item>

                <Form.Item
                  label="Time"
                  name="time"
                  rules={[{ required: true, message: 'Please enter time' }]}
                >
                  <TimePicker placeholder="Time" format={'HH:mm'} />
                </Form.Item>

                <Form.Item
                  label="Home Team Points"
                  name="hometeampoints"
                  rules={[
                    {
                      required: false,
                      message: 'Please enter winner points',
                    },
                  ]}
                >
                  <Input placeholder="Points" />
                </Form.Item>

                {selectedSport.type === 'Draw' ? (
                  <Form.Item
                    label="Draw Points"
                    name="drawSport"
                    rules={[
                      {
                        required: false,
                        message: 'Please enter ',
                      },
                    ]}
                  >
                    <Input placeholder="Points" />
                  </Form.Item>
                ) : null}

                <Form.Item
                  label="Away Team Points"
                  name="awayteampoints"
                  rules={[
                    {
                      required: false,
                      message: 'Please enter winner points',
                    },
                  ]}
                >
                  <Input placeholder="Points" />
                </Form.Item>

                <Form.Item
                  label="Select Home Team"
                  name="hometeam"
                  rules={[
                    {
                      required: true,
                      message: 'Please select Home Team',
                    },
                  ]}
                >
                  <Select onChange={handleHomeTeamSelect}>
                    {selectHomeTeamData.map((x, index) => (
                      <Option value={x._id} key={index}>
                        {x.teamname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Select Away Team"
                  name="awayteam"
                  rules={[
                    {
                      required: true,
                      message: 'Please select away Team',
                    },
                  ]}
                >
                  <Select onChange={handleAwayTeamSelect}>
                    {selectAwayTeamData.map((x, index) => (
                      <Option value={x._id} key={index}>
                        {x.teamname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Event ID"
                  name="eventId"
                  rules={[
                    {
                      required: false,
                      message: 'Please enter event id',
                    },
                  ]}
                >
                  <Input placeholder={'Event Id'} />
                </Form.Item>

                <Form.Item
                  label="Kingbot Tipping"
                  name="kingbotTipping"
                  rules={[
                    {
                      required: true,
                      message: 'Please select Kingbot Tipping ',
                    },
                  ]}
                >
                  <Select onChange={handleKingbotTippingSelect}>
                    {sportId === undefined || sportId === ''
                      ? null
                      : selectWinnerData.map((x, index) => (
                          <Option value={x.name} key={index}>
                            {x.name}
                          </Option>
                        ))}
                  </Select>
                </Form.Item>
              </Form>
            </CustomModal>

            {/* Edit Game Modal */}
            <CustomModal
              handleCancel={handleEditModalCancel}
              handleOk={handleEditModalOk}
              visible={editModalVisible}
              title="Edit Game"
              backBtnText="Back"
              submitBtnText="Submit"
            >
              <Form form={editForm} {...layout}>
                <Form.Item
                  label="Select Sports"
                  name="esport"
                  rules={[
                    {
                      required: true,
                      message: 'Please select sports',
                    },
                  ]}
                >
                  <Select onChange={handleSportSelect}>{sportOptions}</Select>
                </Form.Item>

                <Form.Item
                  label="Select Round"
                  name="eround"
                  rules={[
                    {
                      required: true,
                      message: 'Please select round',
                    },
                  ]}
                >
                  <Select onChange={handleRoundSelect}>
                    {selectRoundData.map((x, index) => (
                      <Option value={x._id} key={index}>
                        {x.roundname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Timezone"
                  name="etimezone"
                  initialValue="Australia/Sydney"
                  rules={[
                    {
                      required: false,
                      message: 'Please enter timezone',
                    },
                  ]}
                >
                  <Input placeholder="Timezone" disabled />
                </Form.Item>

                <Form.Item
                  label="Date"
                  name="edate"
                  initialValue={moment(new Date(editGameDate as any), 'YYYY/MM/DD')}
                  rules={[{ required: true, message: 'Please enter date' }]}
                >
                  <DatePicker onChange={onEditDateChange} placeholder="Date" />
                </Form.Item>

                <Form.Item
                  label="Time"
                  name="etime"
                  initialValue={moment(new Date(editGameTime as any), 'HH:mm')}
                  rules={[{ required: true, message: 'Please enter time' }]}
                >
                  <TimePicker onChange={onEditTimeChange} format={'HH:mm'} placeholder="Time" />
                </Form.Item>

                <Form.Item
                  label="Home Team Points"
                  name="ehometeampoints"
                  rules={[
                    {
                      required: false,
                      message: 'Please enter winner points',
                    },
                  ]}
                >
                  <Input placeholder="Points" />
                </Form.Item>

                {selectedSport.type === 'Draw' ? (
                  <Form.Item
                    label="Draw Points"
                    name="edrawSport"
                    rules={[
                      {
                        required: false,
                        message: 'Please enter ',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                ) : null}

                <Form.Item
                  label="Away Team Points"
                  name="eawayteampoints"
                  rules={[
                    {
                      required: false,
                      message: 'Please enter winner points',
                    },
                  ]}
                >
                  <Input placeholder="Points" />
                </Form.Item>

                <Form.Item
                  label="Select Home Team"
                  name="ehometeam"
                  rules={[
                    {
                      required: true,
                      message: 'Please select Home Team',
                    },
                  ]}
                >
                  <Select onChange={handleHomeTeamSelect}>
                    {selectHomeTeamData.map((x, index) => (
                      <Option value={x._id} key={index}>
                        {x.teamname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Select Away Team"
                  name="eawayteam"
                  rules={[
                    {
                      required: true,
                      message: 'Please select away Team',
                    },
                  ]}
                >
                  <Select onChange={handleAwayTeamSelect}>
                    {selectAwayTeamData.map((x, index) => (
                      <Option value={x._id} key={index}>
                        {x.teamname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Event ID"
                  name="eEventId"
                  rules={[
                    {
                      required: false,
                      message: 'Please enter event id',
                    },
                  ]}
                >
                  <Input placeholder="Event ID" />
                </Form.Item>

                <Form.Item
                  label="Kingbot Tipping"
                  name="ekingbotTipping"
                  rules={[
                    {
                      required: true,
                      message: 'Please select Kingbot Tipping ',
                    },
                  ]}
                >
                  <Select
                    onChange={handleKingbotTippingSelect}
                    disabled={editGameState !== 'open'}
                  >
                    {selectWinnerData.map((x, index) => (
                      <Option value={x.name} key={index}>
                        {x.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Form>
            </CustomModal>

            {/* View Game Modal */}
            <CustomModal
              handleCancel={handleViewModalCancel}
              handleOk={handleViewModalOk}
              visible={viewModalVisible}
              title="View Game Details"
              backBtnText="Back"
              submitBtnText="OK"
            >
              <Descriptions
                title={'Game Details (State: ' + viewGameData.gameState + ')'}
                layout="vertical"
              >
                <Descriptions.Item label="Sports">
                  {viewSportData.sportname}
                </Descriptions.Item>
                <Descriptions.Item label="Season">
                  {viewGameData.season}
                </Descriptions.Item>
                <Descriptions.Item label="Round No">
                  {viewRoundData.roundno}
                </Descriptions.Item>
                <Descriptions.Item label="Round Name">
                  {viewRoundData.roundname}
                </Descriptions.Item>
                <Descriptions.Item label="Round Start Date">
                  {new Date(viewRoundData.startDate).toDateString()}
                </Descriptions.Item>
                <Descriptions.Item label="Round End Date">
                  {new Date(viewRoundData.endDate).toDateString()}
                </Descriptions.Item>
                <Descriptions.Item label="Home Team">
                  {viewHomeTeamData.teamname}
                </Descriptions.Item>
                <Descriptions.Item label="Away Team">
                  {viewAwayTeamData.teamname}
                </Descriptions.Item>
                <Descriptions.Item label="Home Team Points">
                  {viewGameData.homeTeamPoints}
                </Descriptions.Item>
                <Descriptions.Item label="Away Team Points">
                  {viewGameData.awayTeamPoints}
                </Descriptions.Item>
                <Descriptions.Item label="Date">
                  {new Date(viewGameData.gameDate).toDateString()}
                </Descriptions.Item>
                <Descriptions.Item label="Time">
                  {moment(new Date(viewGameData.gameTime)).format('HH:mm')}
                </Descriptions.Item>
                <Descriptions.Item label="Winner">
                  {viewGameData.winningTeam !== '' ? viewGameData.winningTeam : 'NA'}
                </Descriptions.Item>
                <Descriptions.Item label="Home Topsport Odds">
                  {viewGameData.homeTopTipperPoints}
                </Descriptions.Item>
                <Descriptions.Item label="Away Topsport Odds">
                  {viewGameData.awayTopTipperPoints}
                </Descriptions.Item>
                <Descriptions.Item label="Draw Points">
                  {viewGameData.drawPoints}
                </Descriptions.Item>
                <Descriptions.Item label="Event ID">
                  {viewGameData.eventId}
                </Descriptions.Item>
                <Descriptions.Item label="Kingbot Tipping">
                  {viewGameData.kingbotTipping}
                </Descriptions.Item>
              </Descriptions>
            </CustomModal>

            {/* Result Modal */}
            <CustomModal
              handleCancel={handleResultModalCancel}
              handleOk={handleResultModalOk}
              visible={resultModalVisible}
              title="Update Game Result"
              backBtnText="Back"
              submitBtnText="Submit"
            >
              <div style={{ display: 'grid' }}>
                <Card style={{ marginBottom: '10px' }}>
                  <Meta
                    avatar={
                      <Avatar
                        style={{ width: '50px', height: '50px' }}
                        src={viewHomeTeamData.teamLogo}
                      />
                    }
                    title={viewHomeTeamData.teamname}
                    description="Home Team"
                  />
                </Card>
                <Card style={{ marginBottom: '20px' }}>
                  <Meta
                    avatar={
                      <Avatar
                        style={{ width: '50px', height: '50px' }}
                        src={viewAwayTeamData.teamLogo}
                      />
                    }
                    title={viewAwayTeamData.teamname}
                    description="Away Team"
                  />
                </Card>
              </div>

              <Select style={{ width: 200 }} onChange={handleWinnerSelect}>
                {selectWinnerData.map((x, index) => (
                  <Option value={x.name} key={index}>
                    {x.name}
                  </Option>
                ))}
              </Select>
            </CustomModal>
          </div>
        </Spin>
      </div>
    </>
  );
};

export default Game;
